// "Etsy'ye yuklemeye hazir mi?" — TEK kural seti.
//
// Once dort yerde (Listing Builder, iki Liste Denetimi, sunucudaki yayin)
// farkli kurallar vardi; ayni taslak birinde hazir, digerinde hatali
// cikiyordu. Artik hepsi bunu cagirir:
//   - blockers: Etsy reddeder / yanlis acilir -> yayin engellenir
//   - warnings: yayinlanir ama satisi zayiflatir (az etiket, az gorsel...)
// Taslagin "Hazir" durumu da bundan turer (engeli yoksa hazir).

import { comboPrice, type PricedOption } from "./pricing";

export const ETSY_LIMITS = {
  titleMax: 140,
  tagMax: 13,
  tagCharMax: 20,
  imageMax: 20,
  variationAxesMax: 3,
  /** Fiyat/stok TUM eksenlere bagliysa */
  combosAllOnProperty: 400,
  combosMax: 2500,
  personalizationQuestionsMax: 5,
} as const;

/** Listing Builder bolumleri — eksik tiklaninca ilgili bolume gidilir */
export type ReadinessSection =
  | "title" | "images" | "video" | "description" | "tags" | "pricing"
  | "inventory" | "details" | "variations" | "personalization" | "shipping" | "settings";

/** Fiziksel urunde asla gecmemesi gereken dijital urun terimleri. Ilham bir
 *  PNG/dijital listesiyse metne sizabiliyor (liste 4585294329). "sublimation"
 *  tek basina degil: kupa/termos gercekten sublimasyonla basiliyor. */
export const DIGITAL_TERMS = [
  "png", "svg", "dxf", "eps", "jpg", "clipart", "clip art", "digital download", "instant download",
  "digital file", "printable", "cut file", "cricut", "sublimation design", "sublimation png",
  "dtf transfer", "dtf file", "dtg file", "300 dpi", "commercial use", "transparent background",
];

/** Metinde gecen dijital terimler (kelime sinirinda, buyuk/kucuk harf farksiz) */
export function findDigitalTerms(text: string, terms: string[] = DIGITAL_TERMS): string[] {
  // "This is not a digital download." aliciyi uyarir; terim sayilmaz
  const t = ` ${text.toLowerCase().replace(/\b(not|no)\s+(an?\s+)?(digital download|instant download|digital file|png)\b/g, " ")} `;
  return terms.filter((x) => x && new RegExp(`(^|[^a-z0-9])${x.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}s?([^a-z0-9]|$)`).test(t)); // cogul de sayilir: "sublimation designs", "cut files"
}

/** Fiziksel urun metninden dijital terimleri ayiklar: basliktan terimli virgul
 *  obegi, etiketlerden terimli etiket, aciklamadan terimli cumle duser (dosya
 *  turunden bahseden cumle fiziksel urunde zaten yanlistir). */
export function stripDigitalTerms<T extends { title?: string; description?: string; tags?: string[] }>(f: T): T {
  const out = { ...f };
  if (typeof f.title === "string" && findDigitalTerms(f.title).length) {
    out.title = f.title.split(",").map((x) => x.trim()).filter((x) => x && !findDigitalTerms(x).length).join(", ");
  }
  if (Array.isArray(f.tags)) out.tags = f.tags.filter((t) => !findDigitalTerms(String(t)).length);
  if (typeof f.description === "string" && findDigitalTerms(f.description).length) {
    out.description = f.description
      .split("\n")
      .map((line) => line.split(/(?<=[.!?])\s+/).filter((x) => !findDigitalTerms(x).length).join(" "))
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }
  // Hepsi terimliyse bos birakma: orijinal kalir, uyari elle duzeltmeye yonlendirir
  if (out.title !== undefined && !out.title.trim()) out.title = f.title;
  if (out.description !== undefined && !out.description.trim()) out.description = f.description;
  return out;
}

export interface ReadinessIssue {
  code: string;
  section: ReadinessSection;
  message: string;
}

export interface ReadinessInput {
  title?: string | null;
  description?: string | null;
  price?: number | string | null;
  quantity?: number | string | null;
  tags?: string[] | null;
  imageUrls?: string[] | null;
  taxonomyId?: number | string | null;
  shippingProfileId?: string | null;
  listingType?: string | null;
  variations?: Array<{ name?: string; options?: Array<PricedOption & { label?: string }> }> | null;
  personalization?: { enabled?: boolean; questions?: unknown[] } | null;
}

export interface ReadinessResult {
  ready: boolean;
  blockers: ReadinessIssue[];
  warnings: ReadinessIssue[];
}

/** Turkce harf ya da tedarikci kataloğunun Turkce kelimeleri */
export const TURKISH_RE = /[çğıöşüÇĞİÖŞÜ]|\b(yok|sayısı|sayisi|zincir|taş|tas|adet|uzunluk|renk|singapur|gourmet)\b/i;

/** Kombinasyon sayisi (varyasyon yoksa 0) */
export function listingComboCount(variations?: ReadinessInput["variations"]): number {
  if (!variations?.length) return 0;
  return variations.reduce((n, v) => n * (v.options?.length || 0), 1);
}

/** Fiyati degisen eksen sayisi (secenekler arasinda farkli fiyat) */
function pricedAxisCount(variations: NonNullable<ReadinessInput["variations"]>): number {
  return variations.filter((v) => {
    const prices = new Set((v.options ?? []).map((o) => comboPrice(0, [o])));
    return prices.size > 1;
  }).length;
}

/** Etsy'nin bu varyasyonlar icin kabul edecegi urun tavani. Sunucu fiyati tek
 *  eksene baglayabiliyorsa 2500; birden fazla eksen fiyatliysa HEPSINE
 *  baglanir ve tavan 400 olur (Etsy "ya bir ya hepsi" kurali). */
export function listingComboCap(variations?: ReadinessInput["variations"]): number {
  if (!variations?.length) return ETSY_LIMITS.combosMax;
  const priced = pricedAxisCount(variations);
  if (priced === 0) return ETSY_LIMITS.combosMax;
  if (priced === 1 && variations.length > 1) return ETSY_LIMITS.combosMax;
  return ETSY_LIMITS.combosAllOnProperty;
}

export function checkListingReadiness(input: ReadinessInput): ReadinessResult {
  const blockers: ReadinessIssue[] = [];
  const warnings: ReadinessIssue[] = [];
  const B = (code: string, section: ReadinessSection, message: string) => blockers.push({ code, section, message });
  const W = (code: string, section: ReadinessSection, message: string) => warnings.push({ code, section, message });

  const title = (input.title ?? "").trim();
  const description = (input.description ?? "").trim();
  const tags = (input.tags ?? []).filter((t) => String(t).trim());
  const images = input.imageUrls ?? [];
  const variations = (input.variations ?? []).filter((v) => (v.options?.length ?? 0) > 0);
  const physical = (input.listingType || "physical") === "physical";
  const price = Number(input.price);

  // Baslik
  if (!title) B("no_title", "title", "Başlık yok");
  else if (title.length > ETSY_LIMITS.titleMax) B("title_too_long", "title", `Başlık ${title.length} karakter — Etsy en fazla ${ETSY_LIMITS.titleMax}`);
  else if (title.length < 40) W("title_short", "title", `Başlık kısa (${title.length} karakter) — aramada daha az görünür`);

  // Gorseller
  if (images.length === 0) B("no_images", "images", "Görsel yok");
  else if (images.length > ETSY_LIMITS.imageMax) B("too_many_images", "images", `${images.length} görsel — Etsy en fazla ${ETSY_LIMITS.imageMax}`);
  else if (images.length < 5) W("few_images", "images", `${images.length} görsel — en az 5 önerilir`);
  if (images.some((u) => typeof u === "string" && u.startsWith("blob:"))) {
    B("temp_image", "images", "Geçici (blob:) görsel adresi var — görseli yeniden yükleyin");
  }

  // Aciklama
  if (!description) B("no_description", "description", "Açıklama yok");
  else if (description.length < 160) W("short_description", "description", "Açıklama çok kısa (160 karakterden az)");

  // Etiketler
  if (tags.length === 0) W("no_tags", "tags", "Etiket yok — Etsy 13 etikete izin veriyor");
  else if (tags.length < ETSY_LIMITS.tagMax) W("few_tags", "tags", `${tags.length}/${ETSY_LIMITS.tagMax} etiket`);
  if (tags.length > ETSY_LIMITS.tagMax) B("too_many_tags", "tags", `${tags.length} etiket — Etsy en fazla ${ETSY_LIMITS.tagMax}`);
  const longTag = tags.find((t) => String(t).length > ETSY_LIMITS.tagCharMax);
  if (longTag) B("tag_too_long", "tags", `"${longTag}" ${String(longTag).length} karakter — etiket en fazla ${ETSY_LIMITS.tagCharMax}`);

  // Fiyat / stok
  if (!(price > 0)) B("no_price", "pricing", "Fiyat yok");
  if (variations.length === 0 && !(Number(input.quantity) >= 1)) B("no_quantity", "pricing", "Stok adedi en az 1 olmalı");

  // Kategori / kargo
  if (!input.taxonomyId) B("no_taxonomy", "details", "Etsy kategorisi seçilmedi — liste yanlış kategoriye açılır");
  if (physical && !input.shippingProfileId) B("no_shipping", "shipping", "Kargo profili seçilmedi");

  // Varyasyonlar
  if (variations.length > ETSY_LIMITS.variationAxesMax) {
    B("too_many_axes", "variations", `${variations.length} varyasyon ekseni — Etsy en fazla ${ETSY_LIMITS.variationAxesMax}`);
  }
  const combos = listingComboCount(variations);
  const cap = listingComboCap(variations);
  if (combos > cap) {
    B("too_many_combos", "variations", cap === ETSY_LIMITS.combosAllOnProperty
      ? `${combos} kombinasyon — birden fazla eksen fiyatlıyken Etsy en fazla ${cap}. Fiyatı tek eksene bağlayın ya da seçenek azaltın.`
      : `${combos} kombinasyon — Etsy en fazla ${cap}`);
  }
  for (const v of variations) {
    if (!v.name?.trim()) B("axis_no_name", "variations", "Adı olmayan varyasyon ekseni var");
    // Tedarikcinin Turkce ekseni/degeri musteriye gitmesin ("Zincir Tipi", "Taş Yok", "Singapur Chain")
    const tr = [v.name ?? "", ...(v.options ?? []).map((o) => o.label ?? "")].find((x) => TURKISH_RE.test(x));
    if (tr) B("turkish_variation", "variations", `Etsy'ye Türkçe varyasyon metni gidiyor: "${tr}" — İngilizce Etsy adını kullanın`);
    const labels = (v.options ?? []).map((o) => String(o.label ?? "").trim().toLowerCase());
    if (labels.some((l) => !l)) B("option_no_label", "variations", `"${v.name ?? "?"}" ekseninde boş seçenek var`);
    const dup = labels.find((l, i) => l && labels.indexOf(l) !== i);
    if (dup) B("option_duplicate", "variations", `"${v.name ?? "?"}" ekseninde tekrar eden seçenek: ${dup}`);
  }

  // Fiziksel urunde dijital urun dili (PNG, digital download...) aliciyi yaniltir
  if (physical) {
    const fields: Array<[ReadinessSection, string, string]> = [["title", "Başlık", title], ["tags", "Etiketler", tags.join(" | ")], ["description", "Açıklama", description]];
    for (const [section, label, text] of fields) {
      const found = findDigitalTerms(text);
      if (found.length) B("digital_terms", section, `${label} dijital ürün terimi içeriyor (${found.join(", ")}) — fiziksel üründe kullanılmaz`);
    }
  }

  // Kisisellestirme
  const questions = input.personalization?.enabled ? input.personalization.questions ?? [] : [];
  if (questions.length > ETSY_LIMITS.personalizationQuestionsMax) {
    B("too_many_questions", "personalization", `${questions.length} kişiselleştirme alanı — Etsy en fazla ${ETSY_LIMITS.personalizationQuestionsMax}`);
  }
  // Etsy cok sorulu kisisellestirmede talimati 120 karakterde keser
  for (const q of questions as Array<{ question_text?: string; instructions?: string }>) {
    const len = (q?.instructions ?? "").length;
    if (len > 120) W("instructions_long", "personalization", `"${q.question_text ?? "?"}" talimatı ${len} karakter — Etsy 120'de keser`);
  }
  const isCustom = tags.some((t) => String(t).toLowerCase() === "custom");
  if (isCustom && questions.length === 0) {
    B("custom_without_personalization", "personalization", "\"custom\" etiketli ama kişiselleştirme alanı yok");
  }

  return { ready: blockers.length === 0, blockers, warnings };
}
